import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { closeDrawer } from '../../../../redux/global/actions';
import { css } from '@emotion/react';
import Loading from 'components/loading';
import { Flex } from 'components/box';
import { Text } from 'components/typography';
import { useSocialLogin } from 'hooks/customer';
import useErrorNotificationHandler from 'hooks/handle-errors';
import GoogleLogo from 'icons/google.svg';
import Script from 'next/script';
import config from '../../../../util/load-config';
import Button from 'components/button';
import { useAddGlobalStatus, useHasGlobalStatus } from 'hooks/app';
import { AuthenticationContext } from 'types/types';
import { useIsMobile } from 'hooks/use-media';
import { WindowGoogle, isGoogleValid, isAuthenticationValid } from './types';
import { LoginMethodEnum } from 'types/third-party/gtm';

declare global {
  interface Window {
    google: WindowGoogle;
  }
}

const SOCIAL_LOGIN_PROVIDER = 'GOOGLE';

const CLIENT_ID = `${config.social.googleAppId}.apps.googleusercontent.com`;

const GOOGLE_STATUS_LOADED = 'google-loaded';
const GOOGLE_STATUS_INITIALIZED = 'google-initialized';

const WIDTH_MOBILE = 280;
const WIDTH_DESKTOP = 250;

const useGoogleAuthWidth = () => {
  const isMobile = useIsMobile();
  return isMobile ? WIDTH_MOBILE : WIDTH_DESKTOP;
};

/**
 * NOTE: content is replaced when google loads in,
 * but we want it to look as similar as possible to reduce the flicker.
 */
const GoogleAuthPlaceholder = ({
  context,
}: {
  context: AuthenticationContext;
}) => (
  <Flex
    px="12px"
    py="10px"
    width={[WIDTH_MOBILE, WIDTH_DESKTOP]}
    border="1px solid"
    borderColor="mediumGrey"
    borderRadius="50px"
    alignItems="center"
  >
    <GoogleLogo
      css={css`
        width: 18px;
        height: 18px;
      `}
    />
    <Text
      color="black"
      fontSize="r"
      fontWeight={500}
      fontFamily="body"
      m="0 auto"
      letterSpacing={0.1}
    >
      {context === AuthenticationContext.LogIn
        ? 'Sign in with Google'
        : 'Sign up with Google'}
    </Text>
  </Flex>
);

const GoogleAuth = ({
  context,
  setError,
}: {
  context: AuthenticationContext;
  setError: (error?: string) => void;
}) => {
  const dispatch = useDispatch();
  const mountErrors = useErrorNotificationHandler();

  /**
   * NOTE: global status is used because the Script.onLoad event only triggers once per "page load".
   * so without it, the second time you render this button in one "page load", it will never load.
   */
  const addGlobalStatus = useAddGlobalStatus();
  const isGoogleLoaded = useHasGlobalStatus(GOOGLE_STATUS_LOADED);
  const isGoogleInitialized = useHasGlobalStatus(GOOGLE_STATUS_INITIALIZED);

  const width = useGoogleAuthWidth();

  const googleInitializedRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const [credentials, setCredentials] = useState<string | undefined>();
  const [disabled, setDisabled] = useState(false);

  const [socialLogin, { loading, error, completed }] = useSocialLogin(
    LoginMethodEnum.GOOGLE
  );

  useEffect(() => setError(error), [error, setError]);

  useEffect(() => {
    if (
      !googleInitializedRef.current &&
      isGoogleLoaded &&
      typeof window !== 'undefined' &&
      typeof window.google !== 'undefined' &&
      isGoogleValid(window.google)
    ) {
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        use_fedcm_for_prompt: true,
        callback: res => {
          if (isAuthenticationValid(res)) {
            setCredentials(res.credential);
          } else {
            mountErrors([
              {
                name: 'Google authentication error',
                message:
                  'Failed to authenticate with Google, please try again later',
              },
            ]);
          }
        },
      });

      addGlobalStatus(GOOGLE_STATUS_INITIALIZED);
      googleInitializedRef.current = true;
    }
  }, [isGoogleLoaded, addGlobalStatus, mountErrors]);

  useEffect(() => {
    if (
      buttonRef.current &&
      isGoogleInitialized &&
      typeof window !== 'undefined' &&
      typeof window.google !== 'undefined' &&
      isGoogleValid(window.google)
    ) {
      /**
       * NOTE: width used to only work as a string (when this was originally built).
       * since then it seems to work as a number as well.
       * but then also recently for some users a string width will throw an error.
       *
       * we can't be certain that the number version will work for all users either though.
       * so we're gonna try with a string, and if that fails then with a number,
       * and finally if that fails we'll disable google auth entirely.
       */
      try {
        try {
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            width: `${width}`,
            shape: 'pill',
            logo_alignment: 'center',
            text:
              context === AuthenticationContext.LogIn
                ? 'signin_with'
                : 'signup_with',
          });
        } catch (e) {
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            width,
            shape: 'pill',
            logo_alignment: 'left',
            text:
              context === AuthenticationContext.LogIn
                ? 'signin_with'
                : 'signup_with',
          });
        }
      } catch (e) {
        setDisabled(true);
        setError("Google login isn't working, please try again later.");
      }
    }
  }, [buttonRef, context, isGoogleInitialized, width, setError]);

  useEffect(() => {
    if (credentials && !completed) {
      socialLogin({
        provider: SOCIAL_LOGIN_PROVIDER,
        accessToken: credentials,
      });
    }
  }, [credentials, socialLogin, completed]);

  useEffect(() => {
    if (completed) {
      dispatch(closeDrawer());
    }
  }, [completed, dispatch]);

  return (
    <>
      <Script
        id="google-gsi-sdk"
        strategy="lazyOnload"
        src="https://accounts.google.com/gsi/client"
        onLoad={() => addGlobalStatus(GOOGLE_STATUS_LOADED)}
      />

      <Flex flexDirection="column">
        <Loading isLoading={!isGoogleLoaded || !isGoogleInitialized || loading}>
          <Button
            width="100%"
            variant="flat"
            ref={buttonRef}
            disabled={disabled}
            borderRadius="50px"
            p={0}
            m={0}
            textTransform="none"
          >
            <GoogleAuthPlaceholder context={context} />
          </Button>
        </Loading>
      </Flex>
    </>
  );
};

export default GoogleAuth;
